import { useState, useCallback, useEffect, useMemo } from "react"
import { LAYER_GROUPS, ALL_LAYERS_FLAT } from "@/lib/explore/constants"
import type { LayerDefinition, LayerPreset } from "@/types/explore"
import { readVersioned, writeVersioned } from "@/lib/explore/utils/versioned-storage"

const QUICK_LAYERS_KEY = "explorerV2_quickLayers"
const QUICK_LAYERS_VERSION = 2
const DEFAULT_QUICK_LAYERS = ["prospects-dossiers", "patrimoine", "plu", "pc"]

const PRESETS_KEY = "explorerV2_layerPresets"
const PRESETS_VERSION = 1

export const useLayerManager = (layerCategoryOrder?: string[]) => {
  const [activeLayers, setActiveLayers] = useState<string[]>(["prospects-dossiers", "patrimoine"])
  const [layerSearch, setLayerSearch] = useState("")
  const [layerFilters, setLayerFilters] = useState<Record<string, Record<string, unknown>>>({})

  const setLayerFilter = useCallback((layerId: string, filterId: string, value: unknown) => {
    setLayerFilters((prev) => ({
      ...prev,
      [layerId]: { ...prev[layerId], [filterId]: value },
    }))
  }, [])

  const [quickLayers, setQuickLayers] = useState<string[]>(() =>
    readVersioned(QUICK_LAYERS_KEY, QUICK_LAYERS_VERSION, DEFAULT_QUICK_LAYERS),
  )

  const [layerPresets, setLayerPresets] = useState<LayerPreset[]>(() => readVersioned(PRESETS_KEY, PRESETS_VERSION, []))

  // Persist
  useEffect(() => {
    writeVersioned(QUICK_LAYERS_KEY, QUICK_LAYERS_VERSION, quickLayers)
  }, [quickLayers])
  useEffect(() => {
    writeVersioned(PRESETS_KEY, PRESETS_VERSION, layerPresets)
  }, [layerPresets])

  const toggleLayer = useCallback((id: string) => {
    setActiveLayers((prev) => (prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id]))
  }, [])

  const toggleQuickLayer = useCallback((id: string) => {
    setQuickLayers((prev) => (prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id]))
  }, [])

  const savePreset = useCallback(
    (name: string) => {
      setLayerPresets((prev) => [...prev, { name, activeLayers, quickLayers }])
    },
    [activeLayers, quickLayers],
  )

  const loadPreset = useCallback((preset: LayerPreset) => {
    setActiveLayers(preset.activeLayers)
    setQuickLayers(preset.quickLayers)
  }, [])

  const deletePreset = useCallback((index: number) => {
    setLayerPresets((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const quickLayerItems = useMemo(
    () => quickLayers.map((id) => ALL_LAYERS_FLAT.find((l) => l.id === id)).filter((l): l is LayerDefinition => !!l),
    [quickLayers],
  )

  const activeLayerItems = useMemo(
    () => activeLayers.map((id) => ALL_LAYERS_FLAT.find((l) => l.id === id)).filter((l): l is LayerDefinition => !!l),
    [activeLayers],
  )

  const activeCount = useMemo(
    () => activeLayers.filter((id) => !quickLayers.includes(id)).length,
    [activeLayers, quickLayers],
  )

  const orderedLayerGroups = useMemo(() => {
    if (!layerCategoryOrder?.length) return LAYER_GROUPS
    const orderMap = new Map(layerCategoryOrder.map((label, i) => [label, i]))
    return [...LAYER_GROUPS].sort((a, b) => {
      const ia = orderMap.get(a.label) ?? Infinity
      const ib = orderMap.get(b.label) ?? Infinity
      return ia - ib
    })
  }, [layerCategoryOrder])

  const filteredLayerGroups = useMemo(() => {
    if (layerSearch.length < 2) return orderedLayerGroups
    const q = layerSearch.toLowerCase()
    return orderedLayerGroups
      .map((group) => {
        const matchedLayers = group.layers.filter((l) => l.name.toLowerCase().includes(q))
        const matchedSubgroups = (group.subgroups || [])
          .map((sub) => ({
            ...sub,
            layers: sub.layers.filter((l) => l.name.toLowerCase().includes(q)),
          }))
          .filter((sub) => sub.layers.length > 0)
        return { ...group, layers: matchedLayers, subgroups: matchedSubgroups }
      })
      .filter((g) => g.layers.length > 0 || (g.subgroups && g.subgroups.length > 0))
  }, [layerSearch, orderedLayerGroups])

  return {
    activeLayers,
    setActiveLayers,
    quickLayers,
    layerSearch,
    setLayerSearch,
    layerPresets,
    layerFilters,
    setLayerFilter,
    toggleLayer,
    toggleQuickLayer,
    savePreset,
    loadPreset,
    deletePreset,
    quickLayerItems,
    activeLayerItems,
    activeCount,
    filteredLayerGroups,
  }
}
